<template>
  <div class="kb-row">
    <h4 class="kb-row-title flex flex-wrap items-center gap-2">
      Member type
      <SharedMemberTypeBadge :member-type="memberType || 'member'" />
    </h4>

    <div
      v-if="!readOnly"
      class="kb-row-aside"
    >
      <SharedMemberTypeActions
        :member="{ ...member, member_type: memberType || 'member' }"
        @confirm-type-change="onTypeChange"
      />
    </div>

    <!-- Role change confirmation dialog -->
    <AdminMemberRoleChangeDialog
      v-if="!readOnly"
      :visible="showRoleChangeConfirmation"
      :member="member"
      :subkrewe="associatedSubkrewe"
      :pending-type="pendingMemberTypeChange"
      @response="handleRoleChangeConfirmation"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Member } from '~/shared/types/member'
import type { SubKrewe } from '~/shared/types/subkrewe'
import type { MemberTypeValue } from '~/shared/types/memberType.js'

interface Props {
  member: Member
  currentType?: string
  associatedSubkrewe?: SubKrewe | null
  readOnly?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['type-change'])

// Define a type for member_type that matches your Member interface
type MemberType = 'member' | 'redshirt' | 'captain' | undefined

// Use the properly typed ref
const memberType = ref<MemberType>(
  (props.currentType || props.member.member_type || 'member') as MemberType,
)

// Role change confirmation dialog state
const showRoleChangeConfirmation = ref(false)
const pendingMemberTypeChange = ref<'member' | 'redshirt' | null>(null)
const associatedSubkrewe = computed(() => props.associatedSubkrewe || null)

// Ensure we only assign valid values in the watchers
watch(() => props.member.member_type, (newVal) => {
  if (newVal && (newVal === 'member' || newVal === 'redshirt' || newVal === 'captain')) {
    memberType.value = newVal
  }
}, { immediate: true })

watch(() => props.currentType, (newVal) => {
  if (newVal && (newVal === 'member' || newVal === 'redshirt' || newVal === 'captain')) {
    memberType.value = newVal as MemberType
  }
}, { immediate: true })

// Pass type change events up to parent
const onTypeChange = (eventData: {
  currentType: MemberTypeValue
  newType: MemberTypeValue
  hasSubkrewe?: boolean
}) => {
  // Special handling for captain demotion
  if (eventData.currentType === 'captain'
    && (eventData.newType === 'member' || eventData.newType === 'redshirt')) {
    // Only these two are offered as demotion targets; the ref is typed to match.
    pendingMemberTypeChange.value = eventData.newType
    showRoleChangeConfirmation.value = true
    return
  }

  emit('type-change', eventData)
}

// Handle role change confirmation
const handleRoleChangeConfirmation = (confirmed: boolean) => {
  showRoleChangeConfirmation.value = false

  if (confirmed && pendingMemberTypeChange.value) {
    // Forward the confirmed type change
    emit('type-change', {
      currentType: 'captain',
      newType: pendingMemberTypeChange.value,
      hasSubkrewe: !!associatedSubkrewe.value,
    })
  }

  // Reset state
  pendingMemberTypeChange.value = null
}
</script>
